/**
 * Générateur — Effectuer des calculs sur des horaires et des durées
 * Attendu ID : 650 | Niveau : 6ème | Domaine : Grandeurs et mesures
 *
 * Types générés : qcm_unique, texte, texte_trou_saisie, relier
 * Labels de référence :
 *   - Calculer un instant final (départ + durée)
 *   - Calculer un instant initial (arrivée − durée)
 *   - Calculer une durée entre deux instants (arrivée − départ)
 *   - Additionner des durées et convertir en h min s
 * Variantes estimées : 2000+
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeRelier, makeTexteTrou,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 650;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_CALC = 'Calculer un instant final, initial ou une durée';

const PRENOMS = [
    { nom: 'Adam', fem: false }, { nom: 'Amélie', fem: true }, { nom: 'Chloé', fem: true },
    { nom: 'Hugo', fem: false }, { nom: 'Inès', fem: true }, { nom: 'Jade', fem: true },
    { nom: 'Karim', fem: false }, { nom: 'Léo', fem: false }, { nom: 'Lucas', fem: false },
    { nom: 'Manon', fem: true }, { nom: 'Sofia', fem: true }, { nom: 'Thomas', fem: false },
    { nom: 'Yanis', fem: false },
];

// ─── Helpers horaires ───

/** Formate minutes sur 2 chiffres */
const pad = (n) => String(n).padStart(2, '0');

/** Formate un horaire « H h MM » */
const fmtH = (h, m) => `${h} h ${pad(m)}`;

/** Formate une durée « D h MM min » ou « MM min » */
const fmtD = (h, m) => {
    if (h === 0) return `${m} min`;
    if (m === 0) return `${h} h`;
    return `${h} h ${pad(m)} min`;
};

/** Normalise un horaire (min peut être ≥ 60 ou < 0) */
const normalize = (h, m) => {
    while (m >= 60) { m -= 60; h += 1; }
    while (m < 0) { m += 60; h -= 1; }
    return { h, m };
};

/** Calcule instant final = départ + durée */
const addDuree = (dh, dm, durH, durM) => normalize(dh + durH, dm + durM);

/** Calcule instant initial = arrivée − durée */
const subDuree = (ah, am, durH, durM) => normalize(ah - durH, am - durM);

// ─── Contextes ───

const CTX_FINAL = [
    { intro: (p) => `Un film commence à`, action: 'Il dure', question: (p) => 'À quelle heure se termine-t-il ?' },
    { intro: (p) => `${p.nom} commence ses devoirs à`, action: `${randomChoice(['Ses devoirs durent', 'Le travail dure'])}`, question: (p) => `À quelle heure ${p.fem ? 'a-t-elle' : 'a-t-il'} fini ?` },
    { intro: (p) => `Un cours de sport commence à`, action: 'Il dure', question: (p) => 'À quelle heure se termine le cours ?' },
    { intro: (p) => `Un train part à`, action: 'Le trajet dure', question: (p) => 'À quelle heure arrive le train ?' },
    { intro: (p) => `${p.nom} commence à cuisiner à`, action: 'La recette prend', question: (p) => `À quelle heure ${p.fem ? 'a-t-elle' : 'a-t-il'} fini ?` },
];

const CTX_INITIAL = [
    { intro: (p) => `${p.nom} doit arriver au collège à`, action: 'Le trajet dure', question: (p) => `À quelle heure doit-${p.fem ? 'elle' : 'il'} partir ?` },
    { intro: (p) => `Un gâteau doit être prêt à`, action: 'La cuisson dure', question: (p) => 'À quelle heure faut-il le mettre au four ?' },
    { intro: (p) => `Le match se termine à`, action: 'Il a duré', question: (p) => 'À quelle heure a-t-il commencé ?' },
    { intro: (p) => `${p.nom} arrive au cinéma à`, action: 'Le trajet a duré', question: (p) => `À quelle heure est-${p.fem ? 'elle' : 'il'} parti${p.fem ? 'e' : ''} ?` },
];

const CTX_DUREE = [
    { intro: (p) => `${p.nom} part de chez ${p.fem ? 'elle' : 'lui'} à`, arrive: `et arrive à`, question: 'Quelle est la durée du trajet ?' },
    { intro: (p) => `Un coureur part à`, arrive: 'et franchit la ligne d\'arrivée à', question: 'Combien de temps a-t-il mis ?' },
    { intro: (p) => `Le cours commence à`, arrive: 'et se termine à', question: 'Quelle est la durée du cours ?' },
    { intro: (p) => `${p.nom} commence à lire à`, arrive: 'et referme son livre à', question: `Combien de temps a duré la lecture ?` },
];

// ─── Génération de données ───

/** Génère un scénario instant final avec contrainte retenue */
const genFinal = () => {
    const dh = randInt(7, 18);
    const dm = randInt(0, 59);
    const durH = randInt(0, 3);
    const durM = randInt(10, 55);
    const { h: rh, m: rm } = addDuree(dh, dm, durH, durM);
    // S'assurer que le résultat ne dépasse pas 23 h 59
    if (rh > 23) return genFinal();
    return { type: 'final', dh, dm, durH, durM, rh, rm, hasRetenue: dm + durM >= 60 };
};

/** Génère un scénario instant initial avec contrainte emprunt */
const genInitial = () => {
    const ah = randInt(10, 22);
    const am = randInt(0, 59);
    const durH = randInt(0, 3);
    const durM = randInt(10, 55);
    const { h: rh, m: rm } = subDuree(ah, am, durH, durM);
    if (rh < 5) return genInitial();
    return { type: 'initial', ah, am, durH, durM, rh, rm, hasEmprunt: am < durM };
};

/** Génère un scénario durée */
const genDuree = () => {
    const dh = randInt(6, 18);
    const dm = randInt(0, 59);
    const durH = randInt(1, 5);
    const durM = randInt(0, 55);
    const { h: ah, m: am } = addDuree(dh, dm, durH, durM);
    if (ah > 23) return genDuree();
    return { type: 'duree', dh, dm, ah, am, durH: durH, durM: durM };
};

/** Construit un énoncé selon le type */
const buildEnonce = (data) => {
    const p = randomChoice(PRENOMS);
    if (data.type === 'final') {
        const ctx = randomChoice(CTX_FINAL);
        return `<p>${ctx.intro(p)} ${fmtH(data.dh, data.dm)}. ${ctx.action} ${fmtD(data.durH, data.durM)}. ${ctx.question(p)}</p>`;
    }
    if (data.type === 'initial') {
        const ctx = randomChoice(CTX_INITIAL);
        return `<p>${ctx.intro(p)} ${fmtH(data.ah, data.am)}. ${ctx.action} ${fmtD(data.durH, data.durM)}. ${ctx.question(p)}</p>`;
    }
    // durée
    const ctx = randomChoice(CTX_DUREE);
    return `<p>${ctx.intro(p)} ${fmtH(data.dh, data.dm)} ${ctx.arrive} ${fmtH(data.ah, data.am)}. ${ctx.question}</p>`;
};

/** Donne la bonne réponse formatée */
const getAnswer = (data) => {
    if (data.type === 'final') return fmtH(data.rh, data.rm);
    if (data.type === 'initial') return fmtH(data.rh, data.rm);
    return fmtD(data.durH, data.durM);
};

/** Génère des distracteurs pour un horaire */
const distractorsHoraire = (h, m, data) => {
    const dSet = new Set();
    // Retenue mal gérée
    if (data.type === 'final' && data.hasRetenue && m + 60 <= 99) {
        // « pas de retenue » : l'élève écrit les minutes non converties
        // (« 7 h 65 »). Borné à 99 (revue 2026-07-31) : « 7 h 105 » à trois
        // chiffres s'auto-élimine visuellement — piège mort.
        dSet.add(fmtH(h - 1, m + 60));
    }
    if (data.type === 'initial' && data.hasEmprunt) {
        // « pas d'emprunt » (revue 2026-07-31) : l'élève soustrait le petit du
        // grand — heures intactes, minutes retournées (10 h 15 − 45 min →
        // « 10 h 30 »). L'ancienne formule (h+1, m+60) fabriquait des horaires
        // impossibles (« 10 h 90 », « 10 h 105 ») qui ne correspondaient à
        // aucun cheminement d'élève. Jamais égal à la bonne réponse : l'emprunt
        // impose une heure d'écart par construction (+ delete(correct) en aval).
        dSet.add(fmtH(data.ah - data.durH, Math.abs(data.am - data.durM)));
    }
    // ±1h
    dSet.add(fmtH(h + 1, m));
    dSet.add(fmtH(h - 1, m));
    // ±10 min
    const n1 = normalize(h, m + 10);
    dSet.add(fmtH(n1.h, n1.m));
    const n2 = normalize(h, m - 10);
    dSet.add(fmtH(n2.h, n2.m));
    // sens inversé pour durée
    if (data.type === 'duree') {
        const wrong = Math.abs(data.dh - data.ah);
        const wrongM = Math.abs(data.dm - data.am);
        dSet.add(fmtD(wrong, wrongM));
    }

    const correct = (data.type === 'duree') ? fmtD(data.durH, data.durM) : fmtH(h, m);
    dSet.delete(correct);
    // Remove invalid ones
    const result = [];
    for (const d of dSet) {
        if (!d.includes('-') && !d.includes('NaN')) result.push(d);
    }
    return shuffle(result).slice(0, 3);
};

/** Génère des distracteurs pour une durée */
const distractorsDuree = (dH, dM) => {
    const dSet = new Set();
    dSet.add(fmtD(dH + 1, dM));
    dSet.add(fmtD(dH - 1 >= 0 ? dH - 1 : 0, dM + (dH - 1 < 0 ? 60 : 0)));
    dSet.add(fmtD(dH, dM + 10 >= 60 ? dM - 10 : dM + 10));
    dSet.add(fmtD(dH, Math.abs(60 - dM))); // confusion 60 - min
    const correct = fmtD(dH, dM);
    dSet.delete(correct);
    return shuffle([...dSet].filter(d => !d.includes('-') && !d.includes('NaN'))).slice(0, 3);
};

// ═══════════════════════════════════════════
// EXERCICE — calcul_horaire
// ═══════════════════════════════════════════

const genCalcQcm = () => {
    const gen = randomChoice([genFinal, genInitial, genDuree]);
    const data = gen();
    const texte = buildEnonce(data);
    const correct = getAnswer(data);

    let distracteurs;
    if (data.type === 'duree') {
        distracteurs = distractorsDuree(data.durH, data.durM);
    } else {
        const rh = data.type === 'final' ? data.rh : data.rh;
        const rm = data.type === 'final' ? data.rm : data.rm;
        distracteurs = distractorsHoraire(rh, rm, data);
    }

    while (distracteurs.length < 3) {
        const h = data.type === 'duree' ? data.durH : data.rh;
        const m = data.type === 'duree' ? data.durM : data.rm;
        distracteurs.push(data.type === 'duree' ? fmtD(h + randInt(1, 2), m) : fmtH(h + randInt(-2, 2), (m + randInt(5, 15)) % 60));
    }

    // Deduplicate
    const allChoices = [correct, ...distracteurs];
    const unique = [...new Set(allChoices)];
    while (unique.length < 4) unique.push(data.type === 'duree' ? fmtD(randInt(1, 5), randInt(0, 55)) : fmtH(randInt(8, 20), randInt(0, 55)));
    const choices = unique.slice(0, 4);

    const payload = makeQcm(choices, correct, texte, opts(L_CALC));
    // sousType STRUCTUREL (revue 2026-08-01) : posé par la branche même qui a
    // choisi le type de calcul — route le coup de pouce adéquat côté élève
    // (contenu survit au POST, canal éprouvé calculatrice/strict).
    payload.contenu = { ...payload.contenu, sousType: data.type };
    payload._meta.generatorKey = 'calcul_horaire';
    return payload;
};

const genCalcTexte = () => {
    // Saisie DÉCOUPÉE en champs numériques (revue 2026-07-31) : l'ancienne
    // saisie libre exigeait « 7 h 05 » au caractère près (strict) — la frappe
    // naturelle « 7h05 » était rejetée à 100 %, « 7 h 5 » aussi. Deux trous
    // ancrés par les unités (h / min) : chaque champ est un nombre pur, la
    // tolérance numérique gère « 5 » = « 05 ». La clé de format reste
    // calcul_horaire_texte (continuité banque) ; le payload est un texte à
    // trous mode saisie.
    // Garde anti-échange (harnais RT-VARIANTE-FAUSSE-VRAIE) : si heures et
    // minutes de la réponse sont numériquement égales (« 7 h 07 »), l'échange
    // des deux trous serait accepté par la tolérance — on retire le tirage.
    let data;
    for (let i = 0; i < 30; i++) {
        data = randomChoice([genFinal, genInitial, genDuree])();
        const a = data.type === 'duree' ? data.durH : data.rh;
        const b = data.type === 'duree' ? data.durM : data.rm;
        const deuxTrous = !(data.type === 'duree' && (data.durH === 0 || data.durM === 0));
        if (!deuxTrous || a !== b) break;
    }
    const texte = buildEnonce(data);

    // Espaces insécables autour des unités : sur mobile, la ligne ne doit
    // jamais casser entre un champ et son « h »/« min » (revue 2026-08-01)
    let phrase;
    if (data.type === 'duree') {
        if (data.durH === 0) phrase = `Réponse : {{${data.durM}}} min.`;
        else if (data.durM === 0) phrase = `Réponse : {{${data.durH}}} h.`;
        else phrase = `Réponse : {{${data.durH}}} h {{${pad(data.durM)}}} min.`;
    } else {
        phrase = `Réponse : {{${data.rh}}} h {{${pad(data.rm)}}}.`;
    }

    const payload = makeTexteTrou(phrase, texte, { ...opts(L_CALC), mode: 'saisie', strict: false });
    payload.contenu = { ...payload.contenu, sousType: data.type };
    payload._meta.generatorKey = 'calcul_horaire';
    return payload;
};

const genCalcTrou = () => {
    // Texte à trous guidé — instant final (le plus pédagogique)
    const data = genFinal();
    // Force retenue pour la variante guidée intéressante
    const hasRetenue = data.dm + data.durM >= 60;

    let phrase;
    if (hasRetenue) {
        const midM = data.dm + data.durM;
        const finalH = data.dh + data.durH + 1;
        const finalM = midM - 60;
        // Insécables autour des trous (pas de casse champ/unité sur mobile)
        phrase = `Départ : ${fmtH(data.dh, data.dm)}.\n`
            + (data.durH > 0 ? `On ajoute ${data.durH} h → ${fmtH(data.dh + data.durH, data.dm)}.\n` : '')
            + `On ajoute ${data.durM} min → ${data.dh + data.durH} h {{${midM}}} min.\n`
            + `Comme ${midM} ≥ 60, on convertit : {{${finalH}}} h {{${pad(finalM)}}} min.`;
    } else {
        phrase = `Départ : ${fmtH(data.dh, data.dm)}.\n`
            + (data.durH > 0 ? `On ajoute ${data.durH} h → ${fmtH(data.dh + data.durH, data.dm)}.\n` : '')
            + `On ajoute ${data.durM} min → {{${data.rh}}} h {{${pad(data.rm)}}}.`;
    }

    const p = randomChoice(PRENOMS);
    const ctx = randomChoice(CTX_FINAL);
    const texte = `<p>${ctx.intro(p)} ${fmtH(data.dh, data.dm)}. ${ctx.action} ${fmtD(data.durH, data.durM)}. Complète le calcul.</p>`;

    const payload = makeTexteTrou(phrase, texte, { ...opts(L_CALC), mode: 'saisie', strict: false });
    payload.contenu = { ...payload.contenu, sousType: 'final' }; // toujours une addition
    payload._meta.generatorKey = 'calcul_horaire';
    return payload;
};

const genCalcRelier = () => {
    // MONO-TYPE (revue 2026-07-31) : l'ancien panachage donnait 2 paires par
    // simple lecture des préfixes de cibles (« Départ : », « Durée : ») et la
    // fin par élimination — UN calcul résolvait tout. 4 situations du MÊME
    // type : préfixes identiques, 4 calculs obligatoires. Le type varie
    // d'un exercice à l'autre.
    const genType = randomChoice([genFinal, genInitial, genDuree]);
    const types = [genType, genType, genType, genType];
    const pairs = [];
    const usedAnswers = new Set();
    let sousTypeTirage = 'final';

    for (let i = 0; i < 4; i++) {
        let data, source, target, attempts = 0;
        do {
            data = types[i]();
            if (data.type === 'final') {
                source = `Départ ${fmtH(data.dh, data.dm)}, durée ${fmtD(data.durH, data.durM)}`;
                target = `Arrivée : ${fmtH(data.rh, data.rm)}`;
            } else if (data.type === 'initial') {
                source = `Arrivée ${fmtH(data.ah, data.am)}, durée ${fmtD(data.durH, data.durM)}`;
                target = `Départ : ${fmtH(data.rh, data.rm)}`;
            } else {
                source = `Départ ${fmtH(data.dh, data.dm)}, arrivée ${fmtH(data.ah, data.am)}`;
                target = `Durée : ${fmtD(data.durH, data.durM)}`;
            }
            attempts++;
        } while (usedAnswers.has(target) && attempts < 20);
        usedAnswers.add(target);
        pairs.push({ source, target });
        sousTypeTirage = data.type;   // mono-type : identique pour les 4 paires
    }

    const texte = '<p>Relie chaque situation à sa réponse.</p>';
    const payload = makeRelier(pairs, texte, opts(L_CALC));
    payload.contenu = { ...payload.contenu, sousType: sousTypeTirage };
    payload._meta.generatorKey = 'calcul_horaire';
    return payload;
};

// ─── Registre ───
const GENERATORS = {
    calcul_horaire_qcm_unique:       genCalcQcm,
    calcul_horaire_texte:            genCalcTexte,
    calcul_horaire_texte_trou_saisie: genCalcTrou,
    calcul_horaire_relier:           genCalcRelier,
};

// ─── Catégories ───
const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'calcul_horaire', label: L_CALC, formatsList: buildFormats('calcul_horaire') },
];

// ─── Point d'entrée ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
